import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css, keyframes } from 'styled-components';
import { autoUpdate, useFloating } from '@floating-ui/react';
import { useTheme } from 'foundations-theming/utils';
import { PulserSpotlight } from './PulserSpotlight';
import { ONBOARDING_COLOR } from '../constants/colors';
import { toDOMRect, useStableElements } from '../utils/elementUtils';
import { usePulserContainer } from '../hooks/pulser';
import { useReplaceTargetBorder } from '../hooks/useReplaceTargetBorder';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const POPOVER_DEFAULT_Z_INDEX = 1111;
const CYCLE_DURATION = 7;

// Full-viewport rectangle used as the outer ring of the overlay path. Both the
// outer rect and each per-target cutout wind clockwise, so the `evenodd` fill
// rule is required to punch a hole around every target. (`nonzero` would fill
// the cutout instead because the winding numbers add up.)
const OVERLAY_FULL_RECT = 'M0,0H99999V99999H0Z';
const OVERLAY_COLOR = 'rgba(255, 255, 255, 0.2)';
const OVERLAY_BLUR = 'blur(1.5px)';
const fadeIn = keyframes(["from{opacity:0;}to{opacity:1;}"]);
const pulserAnimation = keyframes(["0%{opacity:0;transform:translate(-50%,-50%) scale(1.5) rotate(0turn);}8%{opacity:0.85;}78%{opacity:0.85;}89%{opacity:0;}100%{opacity:0;transform:translate(-50%,-50%) scale(1.5) rotate(3turn);}"]);
const PulserBorderElement = styled.div.withConfig({
  displayName: "UITourPulser__PulserBorderElement"
})(["position:absolute;border-radius:", "px;overflow:hidden;padding:", "px;-webkit-mask:linear-gradient(#fff 0 0) content-box,linear-gradient(#fff 0 0);-webkit-mask-composite:xor;mask-composite:exclude;pointer-events:none;&::before{content:'';background:conic-gradient( transparent var(--shepherd-grad-gap-start,60deg),", " var(--shepherd-grad-solid-start,180deg) var(--shepherd-grad-solid-end,240deg),transparent var(--shepherd-grad-gap-end,340deg) );position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);aspect-ratio:1;width:var(--shepherd-pulser-size,100%);", ";}"], p => p.$borderRadius, p => p.$borderSize, p => p.$borderColor, css(["animation:", " ", "s linear infinite;"], pulserAnimation, CYCLE_DURATION));
const PulserLayer = styled.div.withConfig({
  displayName: "UITourPulser__PulserLayer"
})(["", " position:fixed;inset:0;pointer-events:none;z-index:", ";"], p => p.$animate !== false && css(["animation:", " 0.3s ease-out forwards;"], fadeIn), p => p.$zIndex);

// SVG overlay that frosts the page around the target. Rendered as an SVG path
// (rather than a clip-path'd div) because that is the approach the legacy
// pulser uses and it reliably paints the fill while cutting the hole; a
// clip-path + backdrop-filter div is not rendered consistently across browsers.
// The path captures pointer events over the dimmed area so the user can only
// interact with the highlighted target showing through the cutout.
// Sized with viewport units on a fixed element (matching the legacy layer) so
// it fills the screen on first paint; percentage sizing left the SVG viewport
// undefined until a resize forced a re-layout.
const PulserOverlayLayer = styled.svg.withConfig({
  displayName: "UITourPulser__PulserOverlayLayer"
})(["position:fixed;top:0;left:0;height:100vh;width:100vw;overflow:hidden;pointer-events:none;.tour-pulser-overlay{pointer-events:all;}"]);

// Applies the backdrop blur with a mask-image hole so the target stays sharp.
// The mask is an inline SVG with the same even-odd cutout path used by the
// color overlay: white = visible (blurred), transparent hole = unblurred target.
const PulserBlurLayer = styled.div.withConfig({
  displayName: "UITourPulser__PulserBlurLayer"
})(["position:fixed;top:0;left:0;height:100vh;width:100vw;backdrop-filter:", ";-webkit-mask-image:", ";mask-image:", ";pointer-events:none;"], OVERLAY_BLUR, p => p.$maskSvg, p => p.$maskSvg);
const pulserRects = {
  name: 'rects',
  fn({
    x,
    y,
    rects
  }) {
    return {
      x,
      y,
      data: Object.assign({}, rects.reference, {
        x,
        y
      })
    };
  }
};
const ModernBorder = ({
  borderColor,
  borderRadius: borderRadiusProp,
  borderSize,
  cutoutKey,
  enableBorder,
  onCutoutChange,
  padding,
  target
}) => {
  const theme = useTheme();
  const {
    refs,
    middlewareData
  } = useFloating({
    elements: {
      reference: target
    },
    placement: 'top-start',
    strategy: 'fixed',
    middleware: [pulserRects],
    whileElementsMounted: autoUpdate
  });
  const {
    x = 0,
    y = 0,
    width = 0,
    height = 0
  } = (middlewareData === null || middlewareData === void 0 ? void 0 : middlewareData.rects) || {};
  useEffect(() => {
    if (refs.reference.current !== target) {
      refs.setPositionReference(target);
    }
  }, [refs, target]);
  const initialTotalWidth = width + padding * 2;
  const initialTotalHeight = height + padding * 2;

  // Widen the visible conic-gradient arc for large elements so the sweep
  // stays perceptible across the full border. Interpolates between the
  // default stops (small, ≤100px) and wider stops (large, ≥500px).
  const maxDim = Math.max(initialTotalWidth, initialTotalHeight);
  const sizeFactor = Math.max(0, Math.min(1, (maxDim - 100) / 400));
  const lerp = (a, b) => Math.round(a + (b - a) * sizeFactor);

  // Derive pulser radius from the element's own radius plus the gap (padding),
  // following the concentric-corner rule so the spacing looks consistent.
  // Falls back to the default for elements with no radius.
  const elementRadius = parseFloat(window.getComputedStyle(target).borderTopLeftRadius) || 0;
  const defaultBorderRadius = parseFloat(theme.borderRadius['transitional-container-alt']);
  const borderRadius = borderRadiusProp || (elementRadius > 0 ? elementRadius + padding : defaultBorderRadius);

  // Increase padding just enough so the element's corner arc never overlaps
  // the visible pulser border at the diagonal. Derived from the condition that
  // the element corner must lie inside the pulser's transparent cutout:
  //   padding >= (rOuter − rInner) × (1 − 1/√2) + borderSize / √2
  const minPadding = (borderRadius - elementRadius) * (1 - 1 / Math.SQRT2) + borderSize / Math.SQRT2;
  const effectivePadding = Math.max(padding, minPadding);
  const totalWidth = width + effectivePadding * 2;
  const totalHeight = height + effectivePadding * 2;
  const effectiveMaxDim = Math.max(totalWidth, totalHeight);
  const style = {
    top: y - effectivePadding,
    left: x - effectivePadding,
    width: totalWidth,
    height: totalHeight,
    '--shepherd-pulser-size': `${effectiveMaxDim}px`,
    '--shepherd-grad-gap-start': `${lerp(60, 20)}deg`,
    '--shepherd-grad-solid-start': `${lerp(180, 80)}deg`,
    '--shepherd-grad-solid-end': `${lerp(240, 300)}deg`,
    '--shepherd-grad-gap-end': `${lerp(340, 350)}deg`
  };

  // Report a rounded-rect cutout matching the border's outer edge so the
  // overlay can punch a hole around this target. Skipped until the target has
  // been measured to avoid a degenerate hole at the origin.
  const hasRect = width > 0 && height > 0;
  const cutoutPath = hasRect ? PulserSpotlight.createRectPath(toDOMRect({
    x,
    y,
    width,
    height
  }, effectivePadding), {
    padding: effectivePadding,
    radius: borderRadius,
    targetRect: {
      x,
      y,
      width,
      height
    }
  }) : null;
  useEffect(() => {
    if (!onCutoutChange) {
      return undefined;
    }
    onCutoutChange(cutoutKey, cutoutPath);
    return () => onCutoutChange(cutoutKey, null);
  }, [onCutoutChange, cutoutKey, cutoutPath]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("span", {
      ref: refs.setFloating,
      style: {
        display: 'none'
      }
    }), enableBorder && /*#__PURE__*/_jsx(PulserBorderElement, {
      className: "tour-pulser-border",
      $borderRadius: borderRadius,
      $borderSize: borderSize,
      $borderColor: borderColor,
      style: style
    })]
  });
};
const UITourPulserModern = ({
  attachTo,
  borderColor: borderColorProp,
  borderRadius,
  borderSize = 1.25,
  enableBorder = true,
  enableOverlay = true,
  pulserPadding = 4,
  pulserZIndex = POPOVER_DEFAULT_Z_INDEX,
  replaceTargetBorder = true,
  target
}) => {
  const theme = useTheme();
  const borderColor = borderColorProp !== null && borderColorProp !== void 0 ? borderColorProp : theme.color[ONBOARDING_COLOR];
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const pulserContainer = usePulserContainer(popoverAnchor);
  const pulserTargets = useStableElements(attachTo || target);
  const [cutouts, setCutouts] = useState({});
  const handleCutoutChange = useCallback((key, path) => {
    setCutouts(prev => prev[key] === path ? prev : Object.assign({}, prev, {
      [key]: path
    }));
  }, []);
  const overlayHoles = Object.values(cutouts).filter(path => Boolean(path)).join('');
  const overlayPath = overlayHoles ? `${OVERLAY_FULL_RECT}${overlayHoles}` : undefined;

  // Inline SVG mask for the blur layer: white fill everywhere (= blurred)
  // with even-odd cutout holes that go transparent (= unblurred target).
  const blurMaskSvg = overlayPath ? `url("data:image/svg+xml,${encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' width='${window.innerWidth}' height='${window.innerHeight}'><path d='${overlayPath}' fill='white' fill-rule='evenodd'/></svg>`)}")` : undefined;
  const replacedStyles = useReplaceTargetBorder({
    enabled: replaceTargetBorder,
    targets: pulserTargets
  });

  // When an input border was replaced, match the input's original dimensions
  // instead of using the pulser's defaults
  let effectivePadding = pulserPadding;
  let effectiveBorderSize = borderSize;
  let effectiveBorderRadius = borderRadius;
  if (replacedStyles) {
    effectivePadding = 0;
    effectiveBorderSize = replacedStyles.borderWidth;
    effectiveBorderRadius = replacedStyles.borderRadius;
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("span", {
      ref: setPopoverAnchor,
      "aria-hidden": true,
      style: {
        display: 'none'
      }
    }), pulserContainer && /*#__PURE__*/createPortal( /*#__PURE__*/_jsxs(_Fragment, {
      children: [enableOverlay && overlayPath && blurMaskSvg && /*#__PURE__*/_jsx(PulserBlurLayer, {
        className: "tour-pulser-blur",
        $maskSvg: blurMaskSvg,
        style: {
          zIndex: pulserZIndex
        }
      }), /*#__PURE__*/_jsxs(PulserLayer, {
        className: "tour-pulser-modern",
        $zIndex: pulserZIndex,
        $animate: true,
        children: [enableOverlay && overlayPath && /*#__PURE__*/_jsx(PulserOverlayLayer, {
          className: "tour-pulser-overlay-layer",
          children: /*#__PURE__*/_jsx("path", {
            className: "tour-pulser-overlay",
            d: overlayPath,
            fill: OVERLAY_COLOR,
            fillRule: "evenodd",
            onClickCapture: event => {
              event.stopPropagation();
            }
          })
        }), pulserTargets.map((pulserTarget, index) => {
          const cutoutKey = pulserTarget.id || pulserTarget.getAttribute('data-onboarding') || String(index);
          return /*#__PURE__*/_jsx(ModernBorder, {
            borderColor: borderColor,
            borderRadius: effectiveBorderRadius,
            borderSize: effectiveBorderSize,
            cutoutKey: cutoutKey,
            enableBorder: enableBorder,
            onCutoutChange: enableOverlay ? handleCutoutChange : undefined,
            padding: effectivePadding,
            target: pulserTarget
          }, cutoutKey);
        })]
      })]
    }), pulserContainer)]
  });
};
export default UITourPulserModern;